"use client";
import { useState, useEffect } from "react";
import "../../App.css";
import PageHeader from "../../components/Page/page_header";
import PageContextProvider from "../../components/Page/page_context";
import { useSidebar } from "../../components/ui/sidebar";
import PageContainer from "../../components/Page/page_container";
import { NonogramProvider } from "../../components/Unique/Nonogram/Generator From Image/nonogram_context";
import AdministrationContextProvider from "../../components/Administration/administration_context";
import PageStatisticsHeader from "../../components/Page/page_statistics_header";
import { TilesProvider } from "../../components/Unique/Tiles/tiles_context";
import NonogramPage from "../../components/Unique/Nonogram/NonogramPage";
import AdministrationRendering from "../../components/Administration/administration_rendering";
import TimelineBar from "../../components/Unique/Misc/TimelineBar";
import CalendarChallengeViewer from "../../components/Games/calendar_challenge_viewer";
import GameEmbed, { GAME_LINKS } from "../../components/Games/game_embed";
import { useIsMobile } from "../../hooks/use-mobile";
import { componentPageMap, componentMapping } from "./componentPageMap";
const gamesExcludedFromTimeline = [9, 11, 32];

// Mapping of available context providers
export const contextMapping = {
  "Nonogram Context": NonogramProvider,
  "Administration Context": AdministrationContextProvider,
  "Tiles Context": TilesProvider,
};

const Page = ({ title, containers, cols, type }) => {
  const { game_id, mode } = useSidebar(); // Get game_id from sidebar context
  const isMobile = useIsMobile();

  // Add state for selected date, default to today
  const getToday = () => {
    const today = new Date();
    return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  };
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  // Separate containers into those that need context wrapping and those that don't
  let contextContainers = [];
  let nonContextContainers = [];

  if (containers && containers.length > 0) {
    contextContainers = containers.filter((container) => container.title.includes("Context")); // Containers requiring context
    nonContextContainers = containers.filter((container) => !container.title.includes("Context") && !container.title.includes("Nonogram Viewer")); // Containers without context
  }

  // Function to recursively wrap a component in multiple contexts
  const wrapInContexts = (component, contexts) => {
    if (contexts.length === 0) return component;
    const Context = contextMapping[contexts[0].title]; // Get context from mapping
    if (!Context) {
      console.warn(`Context ${contexts[0].title} not found in contextMapping`); // Warn if context not found
      return wrapInContexts(component, contexts.slice(1)); // Skip to the next context
    }
    return (
      <Context>
        {wrapInContexts(component, contexts.slice(1))} {/* Wrap recursively */}
      </Context>
    );
  };

  // Determine the header title based on container availability
  let header;
  if (containers && containers.length === 0) {
    header = title + " - No Access";
  } else {
    header = title;
  }

  // Initialize state outside the conditional block
  const [containerCols, setContainerCols] = useState(() => {
    const initialCols = Array.from({ length: cols }, () => []);
    return initialCols;
  });

  // Update container columns whenever containers or cols change
  useEffect(() => {
    const containersPerCol = Math.ceil(nonContextContainers.length / cols);
    const newContainerCols = Array.from({ length: cols }, (_, i) => nonContextContainers.slice(i * containersPerCol, (i + 1) * containersPerCol));
    setContainerCols(newContainerCols);
  }, [cols, nonContextContainers]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    // Recalculate initialContainerCols when nonContextContainers or cols change
    const containersPerCol = Math.ceil(nonContextContainers.length / cols);
    const newInitialContainerCols = Array.from({ length: cols }, (_, i) => nonContextContainers.slice(i * containersPerCol, (i + 1) * containersPerCol));
    setContainerCols(newInitialContainerCols);
  }, [nonContextContainers, cols]);

  if (title.toLowerCase().includes("administration")) {
    return <AdministrationRendering title={title} header={header} type={type} game_id={game_id} />;
  }

  if (containers?.[0]?.title?.includes("Existing Nonograms")) {
    return <NonogramPage title={title} containers={containers} cols={cols} type={type} />;
  }

  // If the page is a statistics page, use the original layout
  if (type === "Statistics") {
    const StatisticsPage = require("../../components/Statistics/StatisticsPage").default;
    return wrapInContexts(
      <StatisticsPage title={title} header={header} game_id={game_id} />,
      contextContainers
    );
  }

  // Pass a callback to CalendarChallengeViewer to trigger refresh
  const handleChallengeUpdated = () => {
    // Only refresh the timeline, not the game embed
    setTimelineRefreshKey((k) => k + 1);
  };

  // Callback to refresh calendar after upload
  const handleCalendarRefresh = () => {
    setCalendarRefreshKey((k) => k + 1);
  };

  // For all other pages, use the Sudoblock layout and put normal containers in 'Additional Components'
  return wrapInContexts(
    <PageContextProvider>
      <div className={"default_page_style"}>
        {/* Main page container */}
        <PageHeader title={header} /> {/* Header for the page */}
        <div className={isMobile ? undefined : "px-10"} style={isMobile ? { width: '100vw', padding: '0 0 0 5vw', boxSizing: 'border-box' } : undefined}>
          {gamesExcludedFromTimeline.includes(game_id) ? null : <TimelineBar key={timelineRefreshKey} gameId={game_id} />}
          {/* Responsive layout: row on desktop, column on mobile */}
          <div
            className="row"
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}
          >
            {/* Left column: dynamic width, full width on mobile */}
            <div
              className="col-70"
              style={{ flex: isMobile ? 'unset' : '1 1 0%', minWidth: 0, width: isMobile ? '100%' : undefined }}
            >
              {/* Render planning component if game_id is in componentPageMap */}
              {componentPageMap[game_id] && (
                <div className="mt-4 dark:!bg-[var(--header-color-dark)] bg-white text-black dark:!text-white rounded-lg shadow-md p-4 overflow-x-auto">
                  {componentPageMap[game_id].map((componentName, index) => {
                    const Component = componentMapping[componentName];
                    return Component ? <Component key={index} game_id={game_id} onUpdate={handleCalendarRefresh} /> : null;
                  })}
                </div>
              )}
              {/* Additional Components section below preview - only render if no planning component */}
              {!isMobile && !componentPageMap[game_id] && containerCols.some((containers) => containers.length > 0) && (
                <div className="mt-4 dark:!bg-[var(--header-color-dark)] bg-white text-black dark:!text-white rounded-lg shadow-md p-4 overflow-x-auto">
                  {containerCols.map((containers, colIndex) => (
                    <div className="col" key={colIndex}>
                      {containers.map((container, index) => (
                        <div className="col_full shadow-md mt-4 sm:mt-0" key={index}>
                          <PageContainer title={container.title} game_id={game_id} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Right column: 30% width, full width on mobile */}
            <div
              className="col-30"
              style={{
                flex: isMobile ? 'unset' : '0 0 30%',
                maxWidth: isMobile ? '100%' : '30%',
                width: isMobile ? '100%' : undefined,
              }}
            >
              <div className="mt-4 dark:!bg-[var(--header-color-dark)] bg-white text-black dark:!text-white rounded-lg shadow-md p-4 overflow-x-auto">
                {/* Calendar at the top */}
                <CalendarChallengeViewer key={`${game_id}-${calendarRefreshKey}`} game_id={game_id} onDateChange={setSelectedDate} onChallengeUpdated={handleChallengeUpdated} />
              </div>
              {/* Only render the GameEmbed box if there is a game link for this game_id */}
              {GAME_LINKS[game_id] && (
                <div className="mt-4 dark:!bg-[var(--header-color-dark)] bg-white text-black dark:!text-white rounded-lg shadow-md p-4 overflow-x-auto">
                  {/* GameEmbed below Calendar */}
                  <GameEmbed gameId={game_id} date={selectedDate} width="100%" height="700" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageContextProvider>,
    contextContainers
  );
};

export default Page;
